// components/partial/MaterialsCollectionModal.jsx
// Collects material/product information when completing services
// Renders fields dynamically based on service category configuration

import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { HTTPService } from "../../services/httpService";

const API = new HTTPService();

class MaterialsCollectionModal extends Component {
  constructor(props) {
    super(props);

    // Initialize state with empty values for each service
    const servicesData = {};
    if (props.services && props.services.length > 0) {
      props.services.forEach((service, index) => {
        servicesData[index] = {
          product_brand: '',
          product_name: '',
          roll_number: '',
          template: ''
        };
      });
    }

    this.state = {
      servicesData,
      typeaheadSuggestions: {},
      activeTypeaheadField: null,
      errors: {},
      submitting: false
    };
  }

  componentDidUpdate(prevProps) {
    // Reset state when services change
    if (prevProps.services !== this.props.services && this.props.services) {
      const servicesData = {};
      this.props.services.forEach((service, index) => {
        servicesData[index] = {
          product_brand: '',
          product_name: '',
          roll_number: '',
          template: ''
        };
      });
      this.setState({ servicesData, errors: {} });
    }
  }

  // Get field config for a service's category
  getFieldsForService = (service) => {
    const { categoryConfigs } = this.props;
    const config = categoryConfigs[service.service_category];

    if (!config || !config.data_collect_fields) {
      return [];
    }

    // Parse if string
    if (typeof config.data_collect_fields === 'string') {
      try {
        return JSON.parse(config.data_collect_fields);
      } catch (e) {
        console.error('Error parsing data_collect_fields:', e);
        return [];
      }
    }

    return config.data_collect_fields;
  };

  // Handle field value change
  handleFieldChange = (serviceIndex, fieldName, value) => {
    this.setState(prevState => ({
      servicesData: {
        ...prevState.servicesData,
        [serviceIndex]: {
          ...prevState.servicesData[serviceIndex],
          [fieldName]: value
        }
      },
      errors: {
        ...prevState.errors,
        [`${serviceIndex}_${fieldName}`]: null
      }
    }));

    // Trigger typeahead for brand and product fields
    if ((fieldName === 'product_brand' || fieldName === 'product_name') && value.length >= 2) {
      this.fetchTypeaheadSuggestions(serviceIndex, fieldName, value);
    } else {
      this.setState(prevState => ({
        typeaheadSuggestions: {
          ...prevState.typeaheadSuggestions,
          [`${serviceIndex}_${fieldName}`]: []
        }
      }));
    }
  };

  // Fetch typeahead suggestions from API
  fetchTypeaheadSuggestions = async (serviceIndex, fieldName, query) => {
    const { installerId } = this.props;

    try {
      const response = await API.get(`/materials/typeahead`, {
        installer_id: installerId,
        field: fieldName,
        query: query
      });

      this.setState(prevState => ({
        typeaheadSuggestions: {
          ...prevState.typeaheadSuggestions,
          [`${serviceIndex}_${fieldName}`]: response.data || []
        },
        activeTypeaheadField: `${serviceIndex}_${fieldName}`
      }));
    } catch (error) {
      console.error('Error fetching typeahead suggestions:', error);
    }
  };

  // Select a typeahead suggestion
  handleSelectSuggestion = (serviceIndex, fieldName, value) => {
    this.handleFieldChange(serviceIndex, fieldName, value);
    this.setState(prevState => ({
      typeaheadSuggestions: {
        ...prevState.typeaheadSuggestions,
        [`${serviceIndex}_${fieldName}`]: []
      },
      activeTypeaheadField: null
    }));
  };

  // Hide typeahead dropdown
  handleBlur = (serviceIndex, fieldName) => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      this.setState(prevState => ({
        typeaheadSuggestions: {
          ...prevState.typeaheadSuggestions,
          [`${serviceIndex}_${fieldName}`]: []
        }
      }));
    }, 200);
  };

  // Validate all required fields
  validate = () => {
    const { services } = this.props;
    const { servicesData } = this.state;
    const errors = {};
    let isValid = true;

    services.forEach((service, index) => {
      const fields = this.getFieldsForService(service);

      fields.forEach(field => {
        if (field.required && !servicesData[index]?.[field.field]?.trim()) {
          errors[`${index}_${field.field}`] = `${field.label} is required`;
          isValid = false;
        }
      });
    });

    this.setState({ errors });
    return isValid;
  };

  // Handle form submission
  handleSubmit = () => {
    if (!this.validate()) {
      return;
    }

    const { services, onSubmit } = this.props;
    const { servicesData } = this.state;

    // Build materials data array matching services order
    const materialsDataArray = services.map((service, index) => {
      const fields = this.getFieldsForService(service);
      const data = {};

      // Only include fields that are configured for this category
      fields.forEach(field => {
        const value = servicesData[index]?.[field.field]?.trim();
        if (value) {
          data[field.field] = value;
        }
      });

      return Object.keys(data).length > 0 ? data : null;
    });

    this.setState({ submitting: true });
    onSubmit(materialsDataArray);
  };

  // Check if device is mobile
  isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Render a single field
  renderField = (serviceIndex, field) => {
    const { servicesData, typeaheadSuggestions, errors } = this.state;
    const value = servicesData[serviceIndex]?.[field.field] || '';
    const suggestions = typeaheadSuggestions[`${serviceIndex}_${field.field}`] || [];
    const error = errors[`${serviceIndex}_${field.field}`];
    const showBarcodeScan = field.barcode_scan && this.isMobile();

    return (
      <Form.Group key={field.field} className="mb-3">
        <Form.Label>
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
        </Form.Label>
        <div className="position-relative">
          <div className={showBarcodeScan ? 'd-flex' : ''}>
            <Form.Control
              type="text"
              value={value}
              onChange={(e) => this.handleFieldChange(serviceIndex, field.field, e.target.value)}
              onBlur={() => this.handleBlur(serviceIndex, field.field)}
              placeholder={`Enter ${field.label.toLowerCase()}`}
              isInvalid={!!error}
              className={showBarcodeScan ? 'flex-grow-1' : ''}
            />
            {showBarcodeScan && (
              <Button
                variant="outline-secondary"
                className="ml-2"
                onClick={() => this.handleBarcodeScan(serviceIndex, field.field)}
                title="Scan barcode"
              >
                <i className="fa fa-camera"></i>
              </Button>
            )}
          </div>

          {/* Typeahead dropdown */}
          {suggestions.length > 0 && (
            <div className="typeahead-dropdown">
              {suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className="typeahead-item"
                  onClick={() => this.handleSelectSuggestion(serviceIndex, field.field, suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}

          <Form.Control.Feedback type="invalid">
            {error}
          </Form.Control.Feedback>
        </div>
      </Form.Group>
    );
  };

  // Render fields for a single service
  renderServiceFields = (service, index) => {
    const fields = this.getFieldsForService(service);

    if (fields.length === 0) {
      return null;
    }

    const { services } = this.props;
    const isMultiService = services.length > 1;

    return (
      <div key={index} className={isMultiService ? 'card mb-3' : ''}>
        {isMultiService && (
          <div className="card-header bg-light">
            <strong>{service.description || service.custom_description}</strong>
            <span className="text-muted ml-2">({service.service_category})</span>
          </div>
        )}
        <div className={isMultiService ? 'card-body' : ''}>
          {fields.map(field => this.renderField(index, field))}
        </div>
      </div>
    );
  };

  // Placeholder for barcode scanning - to be implemented
  handleBarcodeScan = (serviceIndex, fieldName) => {
    // TODO: Implement barcode scanner
    alert('Barcode scanning coming soon!');
  };

  render() {
    const { show, onHide, services } = this.props;
    const { submitting } = this.state;

    if (!services || services.length === 0) {
      return null;
    }

    const isMultiService = services.length > 1;
    const serviceName = services[0]?.description || services[0]?.custom_description || 'Service';
    const categoryName = services[0]?.service_category || '';
    const title = isMultiService
      ? `Add Materials Used (${services.length} services)`
      : `Add Materials used for ${categoryName} ${serviceName}`;

    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {isMultiService && (
            <p className="text-muted mb-3">
              Please enter material information for each service below.
            </p>
          )}

          {services.map((service, index) => this.renderServiceFields(service, index))}

          {/* Services with no data collection */}
          {services.some(s => this.getFieldsForService(s).length === 0) && (
            <div className="text-muted mt-3">
              <i className="fa fa-info-circle mr-2"></i>
              Some services don't require material tracking.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={this.handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fa fa-spinner fa-spin mr-2"></i>
                Completing...
              </>
            ) : (
              <>
                <i className="fa fa-check mr-2"></i>
                {isMultiService ? 'Complete All Services' : 'Complete Service'}
              </>
            )}
          </Button>
        </Modal.Footer>

        <style>{`
          .typeahead-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #ced4da;
            border-top: none;
            border-radius: 0 0 4px 4px;
            max-height: 200px;
            overflow-y: auto;
            z-index: 1000;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }

          .typeahead-item {
            padding: 8px 12px;
            cursor: pointer;
            border-bottom: 1px solid #f0f0f0;
          }

          .typeahead-item:hover {
            background-color: #f8f9fa;
          }

          .typeahead-item:last-child {
            border-bottom: none;
          }
        `}</style>
      </Modal>
    );
  }
}

export default MaterialsCollectionModal;
